import type { PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';
import {
	call,
	put,
	race,
	select,
	take,
	takeLatest
} from 'redux-saga/effects';

import { IMapFeaturesActionPayload } from '../../interfaces';

import {
	deleteLayer,
	deleteSource,
	getLayers,
	getSources,
	patchSource,
	postLayer,
	postSource,
	postTileserverUpdate
} from './data';
import { getSourceLayerID, isRequestingUpdateLayer } from './selectors';
import {
	errorReceiveCreateLayer,
	errorReceiveCreateSource,
	errorReceiveLayers,
	errorReceiveRemoveLayer,
	errorReceiveRemoveSource,
	errorReceiveSources,
	errorReceiveUpdateLayer,
	errorReceiveUpdateSource,
	errorReceiveUpdateTileserver,
	failedReceiveCreateLayer,
	failedReceiveCreateSource,
	failedReceiveLayers,
	failedReceiveRemoveLayer,
	failedReceiveRemoveSource,
	failedReceiveSources,
	failedReceiveUpdateLayer,
	failedReceiveUpdateSource,
	failedReceiveUpdateTileserver,
	receiveCreateLayer,
	receiveCreateSource,
	receiveLayers,
	receiveRemoveLayer,
	receiveRemoveSource,
	receiveSources,
	receiveUpdateLayer,
	receiveUpdateSource,
	receiveUpdateTileserver,
	requestCreateLayer as requestCreateLayerAction,
	requestCreateSource as requestCreateSourceAction,
	requestRemoveLayer as requestRemoveLayerAction,
	requestUpdateTileserver as requestUpdateTileserverAction
} from './slice';

export function* requestCreateLayer(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { formData, sourcePayload } = action.payload;
		const response = yield call(postLayer, formData);

		if (response && response.status !== 200) {
			yield put(errorReceiveCreateLayer());
		}
		else if (response && response.status === 200) {
			const { asset_path: assetPath } = response.data;

			yield put(receiveCreateLayer());
			yield put(requestUpdateTileserverAction(assetPath, sourcePayload));
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveCreateLayer());
	}
}

export function* requestCreateSource(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { sourcePayload } = action.payload;
		const response = yield call(postSource, sourcePayload);

		if (response && response.status !== 200) {
			yield put(errorReceiveCreateSource());
		}
		else if (response && response.status === 200) {
			yield put(receiveCreateSource());
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveCreateSource());
	}
}

export function* requestUpdateTileserver(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { assetPath, sourcePayload } = action.payload;
		const response = yield call(postTileserverUpdate, assetPath);

		if (response && response.status !== 204) {
			yield put(errorReceiveUpdateTileserver());
		}
		else if (response && response.status === 204) {
			yield put(receiveUpdateTileserver());

			// NOTE (nc): By having the source param optional, we can control when
			//  to call the requestCreateSource action. This action is not required
			//  when we are performing delete/update on existing layers
			if (sourcePayload) {
				yield put(requestCreateSourceAction(sourcePayload));
			}
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveUpdateTileserver());
	}
}

export function* requestUpdateLayer(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { formData, layerID } = action.payload;

		// First step - remove existing layer
		yield put(requestRemoveLayerAction(layerID));

		const { removeSuccess, removeError, removeFailed } = yield race({
			removeError: take('mapFeatures/errorReceiveRemoveLayer'),
			removeFailed: take('mapFeatures/failedReceiveRemoveLayer'),
			removeSuccess: take('mapFeatures/receiveRemoveLayer')
		});

		if (removeError) {
			yield put(errorReceiveUpdateLayer());
		}
		else if (removeFailed) {
			throw new Error('Layer update failed on remove old layer file.');
		}
		else if (removeSuccess) {
			// Second step - create a new layer
			yield put(requestCreateLayerAction(formData));

			const { createSuccess, createError, createFailed } = yield race({
				createError: take('mapFeatures/errorReceiveCreateLayer'),
				createFailed: take('mapFeatures/failedReceiveCreateLayer'),
				createSuccess: take('mapFeatures/receiveCreateLayer')
			});

			if (createError) {
				yield put(errorReceiveUpdateLayer());
			}
			else if (createFailed) {
				throw new Error('Layer update failed on upload new layer file.');
			}
			else if (createSuccess) {
				// Final step - update tileserver to reflect the changes
				// This will be handled by the requestCreateLayer saga granted that the POST
				// request to create the layer succeeds
				const { updateSuccess, updateError, updateFailed } = yield race({
					updateError: take('mapFeatures/errorReceiveUpdateTileserver'),
					updateFailed: take('mapFeatures/failedReceiveUpdateTileserver'),
					updateSuccess: take('mapFeatures/receiveUpdateTileserver')
				});

				if (updateError) {
					yield put(errorReceiveUpdateLayer());
				}
				else if (updateFailed) {
					throw new Error('Layer update failed on tileserver update.');
				}
				else if (updateSuccess) {
					yield put(receiveUpdateLayer());
				}
			}
		}
	}
	catch (e) {
		yield put(failedReceiveUpdateLayer());
	}
}

export function* requestUpdateSource(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { sourceID, sourcePayload } = action.payload;
		const response = yield call(patchSource, sourceID, sourcePayload);

		if (response && response.status !== 200) {
			yield put(errorReceiveUpdateSource());
		}
		else if (response && response.status === 200) {
			yield put(receiveUpdateSource());
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveUpdateSource());
	}
}

export function* requestRemoveLayer(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { layerID } = action.payload;
		const response = yield call(deleteLayer, layerID);

		if (response && response.status !== 200) {
			yield put(errorReceiveRemoveLayer());
		}
		else if (response && response.status === 200) {
			const { asset_path: assetPath } = response.data;
			const requestingUpdateLayer = yield select(isRequestingUpdateLayer);

			yield put(receiveRemoveLayer());

			// This condition checks whether or not the requestRemoveLayer action was
			// dispatched as part of the requestUpdateLayer flow. If it wasn't then we
			// are safe to dispatch requestUpdateTileserver, otherwise it would cause
			// this action to be dispatched prematurely, and ultimately again after we
			// dispatch requestCreateLayer
			if (!requestingUpdateLayer) {
				yield put(requestUpdateTileserverAction(assetPath));
			}
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveRemoveLayer());
	}
}

export function* requestRemoveSource(action: PayloadAction<IMapFeaturesActionPayload>) {
	try {
		const { sourceID } = action.payload;
		const response = yield call(deleteSource, sourceID);

		if (response && response.status !== 200) {
			yield put(errorReceiveRemoveSource());
		}
		else if (response && response.status === 200) {
			yield put(receiveRemoveSource());

			// remove the associated layer to prevent any dangling layers
			const layerID = yield select(getSourceLayerID, sourceID);

			if (!layerID) {
				throw new Error('No layer found for the target source.');
			}

			yield put(requestRemoveLayerAction(layerID));
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveRemoveSource());
	}
}

export function* requestLayers() {
	try {
		const response = yield call(getLayers);

		if (response && response.status !== 200) {
			yield put(errorReceiveLayers());
		}
		else if (response && response.status === 200) {
			yield put(receiveLayers(response.data));
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveLayers());
	}
}

export function* requestSources() {
	try {
		const response = yield call(getSources);

		if (response && response.status !== 200) {
			yield put(errorReceiveSources());
		}
		else if (response && response.status === 200) {
			yield put(receiveSources(response.data));
		}
		else {
			throw new Error(t('NO_DATA_AVAILABLE'));
		}
	}
	catch (e) {
		yield put(failedReceiveSources());
	}
}

export function* watchRequestCreateLayer() {
	yield takeLatest('mapFeatures/requestCreateLayer', requestCreateLayer);
}

export function* watchRequestCreateSource() {
	yield takeLatest('mapFeatures/requestCreateSource', requestCreateSource);
}

export function* watchRequestUpdateTileserver() {
	yield takeLatest('mapFeatures/requestUpdateTileserver', requestUpdateTileserver);
}

export function* watchRequestUpdateLayer() {
	yield takeLatest('mapFeatures/requestUpdateLayer', requestUpdateLayer);
}

export function* watchRequestUpdateSource() {
	yield takeLatest('mapFeatures/requestUpdateSource', requestUpdateSource);
}

export function* watchRequestRemoveLayer() {
	yield takeLatest('mapFeatures/requestRemoveLayer', requestRemoveLayer);
}

export function* watchRequestRemoveSource() {
	yield takeLatest('mapFeatures/requestRemoveSource', requestRemoveSource);
}

export function* watchRequestLayers() {
	yield takeLatest('mapFeatures/requestLayers', requestLayers);
}

export function* watchRequestSources() {
	yield takeLatest('mapFeatures/requestSources', requestSources);
}
